<!-- routes/GiftCards.svelte -->
<script>
  import { onMount } from "svelte";
  import { _ } from "svelte-i18n";
  import Header from "lib/components/Header.svelte";
  import Footer from "lib/components/Footer.svelte";
  import Container from "lib/components/Container.svelte";
  import Button from "lib/components/Button.svelte";
  import { trackEvent } from "utils/analytics.js";
  import { REDEEM_URL } from "utils/menu.js";

  let isRedirecting = false;

  function handleRedeem() {
    if (isRedirecting) return;
    isRedirecting = true;

    trackEvent("GiftCardRedeemClick", { source: "gift_cards_page" });

    window.location = REDEEM_URL;
  }

  onMount(() => {
    trackEvent("PageView", { page: "GiftCards" });
  });
</script>

<svelte:head>
  <title>Precious Metal Gift Cards - Liquid Noble</title>
  <meta
    name="description"
    content="Give the gift of real gold or silver with a Liquid Noble gift card. Recipients can hold their metal in secure storage or sell it anytime and receive funds in their preferred currency."
  />
</svelte:head>

<Header />

<Container variant="dark" size="large" class="giftcards-container">
  <div class="gc-content">
    <div class="gc-text">
      <h1 class="gc-title">{$_("giftcards.title")}</h1>
      <p class="gc-subtitle">{$_("giftcards.subtitle")}</p>

      <div class="features-list">
        <div class="feature-item">
          <div class="feature-icon">🎁</div>
          <span>{$_("giftcards.feature1")}</span>
        </div>
        <div class="feature-item">
          <div class="feature-icon">🏦</div>
          <div class="currency-content">
            <span>{$_("giftcards.feature2.main")}</span>
            <div class="currency-list">
              <div class="currency-item">
                <img
                  src="/legal/images/Australia.svg"
                  alt="Australia"
                  class="flag-icon"
                />
                <span>AUD</span>
              </div>
              <div class="currency-item">
                <img src="/legal/images/EU.svg" alt="EU" class="flag-icon" />
                <span>EUR</span>
              </div>
              <div class="currency-item">
                <img src="/legal/images/USA.svg" alt="USA" class="flag-icon" />
                <span>USD</span>
              </div>
              <div class="currency-item">
                <img src="/legal/images/UK.svg" alt="UK" class="flag-icon" />
                <span>GBP</span>
              </div>
              <div class="currency-item">
                <img
                  src="/legal/images/New_Zealand.svg"
                  alt="New Zealand"
                  class="flag-icon"
                />
                <span>NZD</span>
              </div>
              <div class="currency-item">
                <img
                  src="/legal/images/Canada.svg"
                  alt="Canada"
                  class="flag-icon"
                />
                <span>CAD</span>
              </div>
              <div class="currency-item">
                <img
                  src="/legal/images/Singapore.svg"
                  alt="Singapore"
                  class="flag-icon"
                />
                <span>SGD</span>
              </div>
              <div class="currency-item">
                <img
                  src="/legal/images/Turkey.svg"
                  alt="Turkey"
                  class="flag-icon"
                />
                <span>TRY</span>
              </div>
            </div>
          </div>
        </div>
        <div class="feature-item">
          <div class="feature-icon">🔒</div>
          <span>{$_("giftcards.feature3")}</span>
        </div>
      </div>

      <div class="redeem-section">
        <div class="redeem-card">
          <div class="redeem-header">
            <div class="redeem-icon">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                <line x1="1" y1="10" x2="23" y2="10"></line>
              </svg>
            </div>
            <div class="redeem-header-text">
              <h2 class="redeem-title">{$_("giftcards.redeem.title")}</h2>
            </div>
          </div>

          <div class="benefits-grid">
            <div class="benefit-item">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              <span>{$_("giftcards.redeem.benefit1")}</span>
            </div>
            <div class="benefit-item">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              <span>{$_("giftcards.redeem.benefit2")}</span>
            </div>
            <div class="benefit-item">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
              <span>{$_("giftcards.redeem.benefit3")}</span>
            </div>
          </div>

          <Button
            variant="primary"
            size="large"
            type="button"
            disabled={isRedirecting}
            class="redeem-button"
            on:click={handleRedeem}
          >
            {isRedirecting
              ? $_("giftcards.redeem.buttonRedirecting")
              : $_("giftcards.redeem.button")}
          </Button>
        </div>
      </div>

      <div class="important-notice">
        <div class="notice-icon">ℹ️</div>
        <div class="notice-text">
          {@html $_("giftcards.notice", {
            values: {
              feesLink: `<a href="/docs/fees">${$_("giftcards.notice.feesLink")}</a>`,
            },
          })}
        </div>
      </div>
    </div>

    <div class="gc-visual">
      <div class="visual-container">
        <div class="gift-card-stack">
          <div class="gift-card gift-card-3">
            <div class="card-shine"></div>
            <div class="card-content">
              <div class="card-label">GOLD</div>
              <div class="card-amount">5.00g</div>
            </div>
          </div>
          <div class="gift-card gift-card-2">
            <div class="card-shine"></div>
            <div class="card-content">
              <div class="card-label">GOLD</div>
              <div class="card-amount">2.50g</div>
            </div>
          </div>
          <div class="gift-card gift-card-1">
            <div class="card-shine"></div>
            <div class="card-content">
              <div class="card-label">GOLD</div>
              <div class="card-amount">1.00g</div>
            </div>
          </div>
        </div>

        <div class="stats-card">
          <div class="stat-item">
            <div class="stat-value">{$_("register.stat1.value")}</div>
            <div class="stat-label">{$_("register.stat1.label")}</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">{$_("register.stat2.value")}</div>
            <div class="stat-label">{$_("register.stat2.label")}</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">{$_("giftcards.stat3.value")}</div>
            <div class="stat-label">{$_("giftcards.stat3.label")}</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</Container>

<Footer />

<style lang="scss">
  .gc-content {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    align-items: center;
    min-height: 45vh;
    margin: 1rem 0;
  }

  .gc-text {
    max-width: 600px;
  }

  .gc-title {
    font-size: 2.8rem;
    font-weight: 800;
    color: var(--text-color1);
    margin-bottom: 1rem;
    line-height: 1.1;
  }

  .gc-subtitle {
    font-size: 1.1rem;
    line-height: 1.6;
    color: var(--text-color2);
    margin-bottom: 1.8rem;
  }

  .features-list {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    margin-bottom: 2rem;
  }

  .feature-item {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    color: var(--text-color2);
    font-size: 0.95rem;
    text-align: left;
  }

  .feature-icon {
    width: 36px;
    height: 36px;
    background: rgba(210, 165, 82, 0.2);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    flex-shrink: 0;
  }

  .currency-content {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .currency-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem 0.6rem;
    align-items: center;
  }

  .currency-item {
    display: flex;
    align-items: center;
    gap: 0.3rem;
    font-size: 0.85rem;
    font-weight: 500;
    color: var(--text-color2);
  }

  .flag-icon {
    width: 16px;
    height: 12px;
    border-radius: 2px;
    object-fit: cover;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
  }

  .redeem-section {
    margin-bottom: 1.5rem;
  }

  .redeem-card {
    background: linear-gradient(
      135deg,
      rgba(210, 165, 82, 0.15) 0%,
      rgba(210, 165, 82, 0.05) 100%
    );
    backdrop-filter: blur(10px);
    border: 1px solid rgba(210, 165, 82, 0.3);
    border-radius: 1.2rem;
    padding: 2rem;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  }

  .redeem-header {
    display: flex;
    gap: 1.2rem;
    align-items: flex-start;
    margin-bottom: 1.8rem;
  }

  .redeem-icon {
    width: 56px;
    height: 56px;
    background: rgba(210, 165, 82, 0.2);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-color1);
    flex-shrink: 0;
  }

  .redeem-header-text {
    flex: 1;
  }

  .redeem-title {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text-color1);
    margin-bottom: 0.5rem;
    line-height: 1.2;
  }

  .benefits-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-bottom: 1.8rem;
  }

  .benefit-item {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    color: var(--text-color2);
    font-size: 0.9rem;

    svg {
      color: var(--text-color1);
      flex-shrink: 0;
    }

    > span {
      text-align: left;
    }
  }

  :global(.redeem-button) {
    width: 100%;
  }

  .important-notice {
    display: flex;
    gap: 0.75rem;
    align-items: flex-start;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 0.8rem;
    padding: 1rem 1.2rem;
    margin-top: 1rem;

    .notice-icon {
      font-size: 0.82rem;
      flex-shrink: 0;
      margin-top: 0.2rem;
      line-height: 1;
    }

    .notice-text {
      font-size: 0.82rem;
      color: rgba(255, 255, 255, 0.6);
      line-height: 1.6;

      :global(a) {
        font-size: 0.82rem;
        color: var(--text-color1);
        text-decoration: none;

        &:hover {
          text-decoration: underline;
        }
      }
    }
  }

  .gc-visual {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .visual-container {
    position: relative;
    width: 100%;
    max-width: 350px;
    height: 360px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .gift-card-stack {
    position: relative;
    width: 260px;
    height: 160px;
    transform: perspective(1000px) rotateY(-15deg) rotateX(8deg);
    animation: float 6s ease-in-out infinite;
  }

  .gift-card {
    position: absolute;
    width: 260px;
    height: 160px;
    border-radius: 14px;
    overflow: hidden;
    box-shadow:
      0 15px 40px rgba(0, 0, 0, 0.5),
      0 5px 15px rgba(255, 215, 0, 0.2),
      inset 0 1px 8px rgba(255, 255, 255, 0.25),
      inset 0 -1px 8px rgba(0, 0, 0, 0.2);
  }

  .gift-card-1 {
    background: linear-gradient(
      135deg,
      #f4d03f 0%,
      #e6ac00 40%,
      #c9a328 80%,
      #b8941f 100%
    );
    z-index: 3;
    top: 0;
    left: 0;
  }

  .gift-card-2 {
    background: linear-gradient(
      135deg,
      #e8c84a 0%,
      #d4a017 40%,
      #b89020 80%,
      #a07d18 100%
    );
    z-index: 2;
    top: 12px;
    left: 12px;
  }

  .gift-card-3 {
    background: linear-gradient(
      135deg,
      #dbbf45 0%,
      #c49015 40%,
      #a07a18 80%,
      #8a6812 100%
    );
    z-index: 1;
    top: 24px;
    left: 24px;
  }

  .card-shine {
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.35) 50%,
      transparent 100%
    );
    animation: shine 4s ease-in-out infinite;
  }

  .gift-card-2 .card-shine {
    animation-delay: 1.3s;
  }

  .gift-card-3 .card-shine {
    animation-delay: 2.6s;
  }

  .card-content {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    padding: 1.5rem;
  }

  .card-label {
    font-size: 0.65rem;
    font-weight: 700;
    letter-spacing: 2px;
    color: rgba(0, 0, 0, 0.5);
    text-transform: uppercase;
    margin-bottom: 0.3rem;
  }

  .card-amount {
    font-size: 1.8rem;
    font-weight: 800;
    color: rgba(0, 0, 0, 0.55);
    text-shadow: 0 1px 3px rgba(255, 255, 255, 0.3);
    line-height: 1;
  }

  .stats-card {
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(255, 255, 255, 0.15);
    backdrop-filter: blur(15px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    padding: 1rem 1.2rem;
    display: flex;
    gap: 1.2rem;
    animation: slideUp 1s ease-out 0.5s both;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  }

  .stat-item {
    text-align: center;
    min-width: 50px;
  }

  .stat-value {
    font-size: 1.1rem;
    font-weight: bold;
    color: var(--text-color1);
    line-height: 1;
    margin-bottom: 0.2rem;
  }

  .stat-label {
    font-size: 0.75rem;
    color: var(--text-color2);
    font-weight: 500;
  }

  @keyframes float {
    0%,
    100% {
      transform: perspective(1000px) rotateY(-15deg) rotateX(8deg)
        translateY(0px);
    }
    50% {
      transform: perspective(1000px) rotateY(-15deg) rotateX(8deg)
        translateY(8px);
    }
  }

  @keyframes shine {
    0% {
      left: -100%;
    }
    50% {
      left: 100%;
    }
    100% {
      left: 100%;
    }
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateX(-50%) translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateX(-50%) translateY(0);
    }
  }

  @media (max-width: 1024px) {
    .gc-content {
      grid-template-columns: 1fr;
      gap: 2rem;
      text-align: center;
    }

    .gc-text {
      max-width: none;
    }

    .feature-item {
      text-align: left;
    }

    .visual-container {
      max-width: 300px;
      height: 280px;
    }

    .gift-card-stack {
      width: 220px;
      height: 135px;
    }

    .gift-card {
      width: 220px;
      height: 135px;
    }
  }

  @media (max-width: 768px) {
    .gc-title {
      font-size: 2.2rem;
    }

    .gc-subtitle {
      font-size: 1rem;
    }

    .benefits-grid {
      grid-template-columns: 1fr;
    }

    .redeem-header {
      flex-direction: column;
      align-items: center;
      text-align: center;
    }

    .currency-list {
      gap: 0.3rem 0.5rem;
    }

    .currency-item {
      font-size: 0.8rem;
    }

    .flag-icon {
      width: 14px;
      height: 10px;
    }

    .stats-card {
      padding: 0.8rem;
      gap: 0.8rem;
    }

    .visual-container {
      height: 220px;
    }

    .gift-card-stack {
      width: 200px;
      height: 125px;
    }

    .gift-card {
      width: 200px;
      height: 125px;
    }
  }

  @media (max-width: 480px) {
    .gc-title {
      font-size: 1.8rem;
    }

    .gc-subtitle {
      font-size: 0.95rem;
    }

    .redeem-card {
      padding: 1.5rem;
    }

    .redeem-title {
      font-size: 1.3rem;
    }

    .feature-item {
      font-size: 0.85rem;
    }

    .feature-icon {
      width: 32px;
      height: 32px;
      font-size: 1rem;
    }

    .currency-list {
      gap: 0.25rem 0.4rem;
    }

    .currency-item {
      font-size: 0.75rem;
    }

    .flag-icon {
      width: 12px;
      height: 9px;
    }

    .important-notice {
      text-align: left;
      margin-top: 0.75rem;
    }
  }
</style>
